/**
 * Styles for the different types of button variant/styles.
 */
import get from "lodash.get";

import {
  css,
  focusOutlineProp,
  hoverBorderColorProp,
  themeGet,
  variant,
} from "@catchoftheday/theme";

import { ButtonProps } from "../types";

const getColorVariant = (props, suffix = "") =>
  get(props.theme, `colors.button.${props.variant}${suffix}`);

/**
 * Styles that apply to all buttons.
 */
function allStyles(props: ButtonProps) {
  return css`
    ${props.block
      ? css`
          width: 100%;
        `
      : ""}

    margin: 0;
    line-height: ${themeGet("lineHeights.normal")(props)};
    text-align: center;
    text-decoration: none;
    border: ${themeGet("borders.2")(props)};
    border-color: transparent;
    border-radius: ${themeGet("radii.default")(props)};
    outline: 0;
    transition: background-color ${themeGet("transitions.default")(props)},
      border-color ${themeGet("transitions.default")(props)};
    cursor: pointer;
    appearance: none;

    background-color: ${getColorVariant(props)};
    color: ${getColorVariant(props, "Text")};

    &:hover:not(:disabled):not([disabled]) {
      background-color: ${getColorVariant(props, "Hover")};
      color: ${getColorVariant(props, "HoverText")};
      ${props.underlineOnHover && `text-decoration: underline;`}
      ${hoverBorderColorProp(props)}
    }

    &:focus[data-focus-visible-added],
    &:focus-visible {
      background-color: ${getColorVariant(props, "Hover")};
    }

    &:disabled {
      background-color: ${getColorVariant(props, "Disabled")};
      color: ${getColorVariant(props, "DisabledText")};
      opacity: ${themeGet("opacity.disabled")(props)};
      cursor: not-allowed;
    }

    &:active {
      box-shadow: ${themeGet("shadows.button")(props)};
    }
  `;
}

type StyleVariant = "plain" | "outlined" | "contained";

function getStyleVariant(props: ButtonProps): StyleVariant {
  if (props.variant === "plain") {
    return "plain";
  }

  if (props.outline) {
    return "outlined";
  }

  return "contained";
}

/**
 * Style by the overall type of button eg outlined vs plain vs contained
 */
function categoryStyles(props: ButtonProps) {
  const realVariant = getStyleVariant(props);

  switch (realVariant) {
    case "outlined": {
      return css`
        ${!props.color && `color: ${getColorVariant(props)}`};

        border-color: ${getColorVariant(props)};
        background-color: white;

        &:hover:not(:disabled):not([disabled]),
        &:focus[data-focus-visible-added],
        &:focus-visible {
          color: white;
          border-color: ${getColorVariant(props, "Hover")};
        }

        &:disabled {
          color: ${themeGet("colors.typography.inactive")(props)};
          border-color: ${themeGet("colors.typography.inactive")(props)};
          background-color: transparent;
        }
      `;
    }
    case "plain": {
      return css`
        &:active {
          box-shadow: none;
        }

        &:focus[data-focus-visible-added],
        &:focus-visible {
          ${focusOutlineProp(props)};
        }
      `;
    }
    case "contained": {
      return css``;
    }
  }
}

/**
 * Styles that apply to specific named variants such as primary/secondary/onePass
 */
const buttonVariantDefs = {
  primary: {
    // You can pass further styling customisation as per below
    // color: "white",
    // bg: "primary",
    // "&:active": {
    //   boxShadow: "button",
    // },
  },
  secondary: {},
  tertiary: {},
  error: {},
  plain: {},
  onePassPrimary: {},
  onePassOutline: {},
};

const specificStyles = variant({
  variants: buttonVariantDefs,
});

/**
 * All variant styles. More specific styles win
 */
export function styleVariant(props: ButtonProps) {
  return css`
    ${allStyles(props)}
    ${categoryStyles(props)}
    ${specificStyles(props)}
  `;
}
